import { Controller } from 'stimulus'

// The survey builder (survey_templates/edit): question cards to add, edit,
// drag into order, and turn into follow-ups, with a live preview of the page
// people answer. The state is JSON (SurveysHelper#survey_builder_state);
// Save PATCHes it to SurveyTemplatesController#questions
// (Surveys::QuestionsPayload / SaveQuestions), and the preview re-renders
// through #preview_questions as you type. A question that already has answers
// keeps its type (One choice and Rating scale, and Short answer and Comment
// box, can still swap), and its preset scale can only become custom; the
// server applies the same rules.
const CHOICE_TYPES = ['rating', 'multiple_choice', 'checkboxes', 'yes_no']
const ICONS = {
  grip: '<svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true"><circle cx="5.5" cy="3" r="1.3"/><circle cx="10.5" cy="3" r="1.3"/><circle cx="5.5" cy="8" r="1.3"/><circle cx="10.5" cy="8" r="1.3"/><circle cx="5.5" cy="13" r="1.3"/><circle cx="10.5" cy="13" r="1.3"/></svg>',
  chev: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 6l4 4 4-4"/></svg>',
  rating: '<svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor" aria-hidden="true"><rect x="1.5" y="9" width="3" height="5.5" rx="1"/><rect x="6.5" y="5.5" width="3" height="9" rx="1"/><rect x="11.5" y="2" width="3" height="12.5" rx="1"/></svg>',
  multiple_choice: '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="8" cy="8" r="6"/><circle cx="8" cy="8" r="2.4" fill="currentColor" stroke="none"/></svg>',
  checkboxes: '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="2" y="2" width="12" height="12" rx="3"/><path d="M5 8.2l2 2 4-4.2"/></svg>',
  yes_no: '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="1.5" y="4" width="13" height="8" rx="4"/><circle cx="11" cy="8" r="2" fill="currentColor" stroke="none"/></svg>',
  text: '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M2 11h12M4 5h5M6.5 5v6"/></svg>',
  text_area: '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M2 4h12M2 8h12M2 12h8"/></svg>'
}

const esc = value => String(value == null ? '' : value)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')

export default class extends Controller {
  static targets = ['list', 'palette', 'status', 'errors', 'screen', 'wideScreen', 'phoneFrame', 'wideFrame', 'scalebox'];

  connect() {
    this.state = JSON.parse(this.data.get('state'))
    this.config = JSON.parse(this.data.get('config'))
    this.openKey = null
    this.seq = 0
    this.deviceMode = 'phone'
    this.dirty = false
    const fields = this.element.querySelectorAll('[data-field]')
    for (let i = 0; i < fields.length; i += 1) fields[i].value = this.state[fields[i].dataset.field] || ''
    this.paletteTarget.innerHTML = Object.keys(this.config.types).map(type => (
      `<button type="button" class="sv-btn-outline is-sm" data-action="survey-builder#add" data-type="${type}">${ICONS[type]}${esc(this.config.types[type])}</button>`
    )).join('')

    this.onBeforeUnload = (event) => {
      if (!this.dirty) return
      event.preventDefault()
    }
    window.addEventListener('beforeunload', this.onBeforeUnload)
    this.onResize = () => this.fitScale()
    window.addEventListener('resize', this.onResize)

    this.listTarget.addEventListener('click', event => this.listClick(event))
    this.listTarget.addEventListener('input', event => this.listInput(event))
    this.listTarget.addEventListener('change', event => this.listChange(event))
    this.render()
    this.enableSorting()
    this.refreshPreview(0)
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
    window.removeEventListener('resize', this.onResize)
    clearTimeout(this.previewTimer)
  }

  // --- model helpers -----------------------------------------------------

  get questions() {
    return this.state.questions
  }

  find(key) {
    return this.questions.find(q => q.key === key)
  }

  optionsOf(question) {
    if (question.answer_type === 'yes_no') return this.config.yesNo
    if (question.answer_type === 'rating' && question.scale !== 'custom') return this.config.scales[question.scale].choices
    return (question.choices || []).map(c => c.label)
  }

  isChoice(question) {
    return CHOICE_TYPES.includes(question.answer_type)
  }

  scoreQuestion() {
    const rated = this.questions.filter(q => q.answer_type === 'rating')
    return rated.find(q => q.key === this.state.score_key) || rated[0]
  }

  sourcesBefore(question) {
    const index = this.questions.indexOf(question)
    return this.questions.slice(0, index).filter(q => this.isChoice(q))
  }

  allowedTypes(question) {
    const types = Object.keys(this.config.types)
    if (!question.answered) return types
    const groups = [['multiple_choice', 'rating'], ['text', 'text_area']]
    const group = groups.find(g => g.includes(question.answer_type))
    return group || [question.answer_type]
  }

  allowedScales(question, wasType) {
    const scales = Object.keys(this.config.scales)
    if (!question.answered) return scales
    if (wasType === 'multiple_choice') return ['custom']
    return Array.from(new Set([question.scale, 'custom']))
  }

  // --- rendering ---------------------------------------------------------

  render() {
    this.listTarget.innerHTML = this.questions.map((q, i) => this.card(q, i)).join('')
      || '<div class="sv-empty">No questions yet. Add one below.</div>'
  }

  card(question, index) {
    const open = this.openKey === question.key
    const score = this.scoreQuestion() === question
    const follow = question.show_if ? this.followLabel(question) : ''
    const chips = `${score ? '<span class="sv-q-chip is-score">Score</span>' : ''}${follow ? `<span class="sv-q-chip is-follow">${esc(follow)}</span>` : ''}<span class="sv-q-chip">${question.required ? 'Required' : 'Optional'}</span>`
    const text = question.question ? esc(question.question) : 'Untitled question'
    return `<div class="sv-q ${open ? 'is-open' : ''}" data-key="${esc(question.key)}">
      <div class="sv-q-head" data-toggle="1">
        <span class="sv-handle" title="Drag to reorder">${ICONS.grip}</span>
        <span class="sv-q-num">${index + 1}</span>
        <span class="sv-q-type">${ICONS[question.answer_type]}${esc(this.config.types[question.answer_type])}</span>
        <span class="sv-q-text ${question.question ? '' : 'is-blank'}">${text}</span>
        <span class="sv-q-chips">${chips}</span>
        <span class="sv-q-chev">${ICONS.chev}</span>
      </div>
      ${open ? this.body(question, index) : ''}
    </div>`
  }

  body(question, index) {
    const key = esc(question.key)
    const types = this.allowedTypes(question)
    const typeSelect = `<select id="sv-qy-${key}" class="sv-in" data-f="answer_type" ${types.length === 1 ? 'disabled' : ''}>${types.map(t => `<option value="${t}" ${t === question.answer_type ? 'selected' : ''}>${esc(this.config.types[t])}</option>`).join('')}</select>`
    let html = `<div class="sv-q-body">
      <div class="sv-q-grid">
        <div><label class="sv-label" for="sv-qt-${key}">Question</label><input id="sv-qt-${key}" class="sv-in" data-f="question" value="${esc(question.question)}" maxlength="500"></div>
        <div><label class="sv-label" for="sv-qy-${key}">Type</label>${typeSelect}</div>
      </div>
      ${question.answered ? `<div class="sv-hint" style="margin-top:-8px;">${this.lockedHint(question)}</div>` : ''}
      <div><label class="sv-label" for="sv-qh-${key}">Help text <span class="sv-faint" style="font-weight:400;">(optional, shown under the question)</span></label><input id="sv-qh-${key}" class="sv-in" data-f="help_text" value="${esc(question.help_text)}" maxlength="500"></div>`

    if (question.answer_type === 'rating') {
      const scales = this.allowedScales(question, question.answeredType || question.answer_type)
      html += `<div><label class="sv-label" for="sv-qs-${key}">Scale</label><select id="sv-qs-${key}" class="sv-in" data-f="scale">${scales.map(s => `<option value="${s}" ${s === question.scale ? 'selected' : ''}>${esc(this.config.scales[s].label)}</option>`).join('')}</select>
        <div class="sv-hint">${question.scale === 'custom' ? 'List the best answer first. Scored from the top down: with 4 choices, the first scores 4 and the last scores 1.' : 'Scored in Results: the best answer scores highest.'}</div></div>`
    }

    if (['multiple_choice', 'checkboxes'].includes(question.answer_type) || (question.answer_type === 'rating' && question.scale === 'custom')) {
      const square = question.answer_type === 'checkboxes' ? 'sq' : ''
      html += `<div><div class="sv-label">${question.answer_type === 'rating' ? 'Choices, best first' : 'Choices'}</div><div class="sv-choices">${
        (question.choices || []).map((c, i) => `<div class="sv-choice"><span class="dot ${square}"></span><input class="sv-in" data-choice="${i}" value="${esc(c.label)}" aria-label="Choice ${i + 1}" maxlength="200"><button type="button" class="x" data-rm="${i}" aria-label="Remove choice">&times;</button></div>`).join('')
      }</div><button type="button" class="sv-link" data-addchoice="1" style="margin-top:8px;">+ Add a choice</button></div>`
    }

    if (question.answer_type === 'rating') {
      html += this.scoreQuestion() === question
        ? '<div class="sv-note"><b>This is the survey\'s score.</b> Its average shows on the Lab Surveys list and leads the Results tab. Every rating question still gets its own bar in Results.</div>'
        : `<div><button type="button" class="sv-link" data-score="1">Use this question for the survey's score</button><div class="sv-hint">The score is now: ${esc((this.scoreQuestion() || {}).question || '')}</div></div>`
    }

    const sources = this.sourcesBefore(question)
    html += `<div class="sv-actions" style="gap:18px;">
      <label class="ui-switch-label"><input type="checkbox" data-f="required" ${question.required ? 'checked' : ''}><span class="ui-switch"></span><span>Required</span></label>
      ${sources.length ? `<label class="ui-switch-label"><input type="checkbox" data-f="follow" ${question.show_if ? 'checked' : ''}><span class="ui-switch"></span><span>Ask only when an earlier answer matches</span></label>` : '<span class="sv-hint" style="margin:0;">Follow-up logic needs a choice question above this one.</span>'}
    </div>`

    if (question.show_if && sources.length) {
      const source = this.find(question.show_if.key) || sources[0]
      html += `<div class="sv-cond"><div class="sv-actions" style="gap:8px;"><span class="sv-label">Ask this when</span>
        <select class="sv-in is-inline" data-f="follow_source" style="max-width:340px;">${sources.map(s => `<option value="${esc(s.key)}" ${s.key === source.key ? 'selected' : ''}>Q${this.questions.indexOf(s) + 1}. ${esc((s.question || 'Untitled').slice(0, 50))}</option>`).join('')}</select>
        <span class="sv-label">is any of</span></div>
        <div class="sv-vchips">${this.optionsOf(source).map((label, i) => `<button type="button" class="sv-vchip ${question.show_if.choices.includes(i) ? 'is-on' : ''}" data-cv="${i}">${esc(label)}</button>`).join('')}</div></div>`
    }

    html += `<div class="sv-q-foot"><div class="sv-actions" style="gap:16px;"><button type="button" class="sv-link" data-dup="1">Duplicate</button><button type="button" class="sv-link is-red" data-del="1">Delete</button></div>
      <span class="sv-hint" style="margin:0;">Rewording also relabels answers already collected. To ask something new, add a question.</span></div></div>`
    return html
  }

  followLabel(question) {
    const source = this.find(question.show_if.key)
    if (!source) return ''
    const options = this.optionsOf(source)
    const picked = question.show_if.choices.filter(i => i < options.length).sort((a, b) => a - b)
    const number = this.questions.indexOf(source) + 1
    if (!picked.length) return `Only if Q${number} is…`
    const contiguous = picked.every((v, i) => i === 0 || v === picked[i - 1] + 1)
    const ascending = ['stars', 'likely'].includes(source.scale)
    if (source.answer_type === 'rating' && contiguous && picked.length > 1) {
      if (!ascending && picked[picked.length - 1] === options.length - 1) return `Only if Q${number} is ${options[picked[0]]} or lower`
      if (ascending && picked[0] === 0) return `Only if Q${number} is ${options[picked[picked.length - 1]]} or lower`
    }
    const names = picked.map(i => options[i])
    return `Only if Q${number} is ${names.length > 2 ? `${names[0]} or ${names.length - 1} more` : names.join(' or ')}`
  }

  lockedHint(question) {
    const type = question.answeredType || question.answer_type
    if (['text', 'text_area'].includes(type)) return 'This question has answers, so it stays a written answer (Short answer or Comment box).'
    if (['multiple_choice', 'rating'].includes(type)) return 'This question has answers, so its type is fixed, except that One choice and a custom Rating scale can swap.'
    return 'This question has answers, so its type is fixed.'
  }

  // Questions that followed this one stop being follow-ups.
  dropFollowUpsOf(key) {
    this.state.questions = this.questions.map(q => (q.show_if && q.show_if.key === key ? { ...q, show_if: null } : q))
  }

  // --- events ------------------------------------------------------------

  listClick(event) {
    const card = event.target.closest('.sv-q')
    if (!card || event.target.closest('.sv-handle')) return
    const question = this.find(card.dataset.key)
    if (event.target.closest('[data-toggle]')) {
      this.openKey = this.openKey === question.key ? null : question.key
      this.render()
      return
    }
    if (event.target.closest('[data-rm]')) {
      question.choices.splice(Number(event.target.closest('[data-rm]').dataset.rm), 1)
      this.changed(true)
      return
    }
    if (event.target.closest('[data-addchoice]')) {
      question.choices = question.choices || []
      question.choices.push({ id: null, label: 'New choice' })
      this.changed(true)
      const inputs = this.listTarget.querySelectorAll(`.sv-q[data-key="${CSS.escape(question.key)}"] [data-choice]`)
      const last = inputs[inputs.length - 1]
      if (last) { last.focus(); last.select() }
      return
    }
    if (event.target.closest('[data-cv]')) {
      const value = Number(event.target.closest('[data-cv]').dataset.cv)
      const list = question.show_if.choices
      const at = list.indexOf(value)
      if (at >= 0) list.splice(at, 1); else list.push(value)
      list.sort((a, b) => a - b)
      this.changed(true)
      return
    }
    if (event.target.closest('[data-score]')) {
      this.state.score_key = question.key
      this.changed(true)
      return
    }
    if (event.target.closest('[data-dup]')) {
      const copy = JSON.parse(JSON.stringify(question))
      copy.key = this.newKey()
      copy.id = null
      copy.answered = false
      copy.choices = (copy.choices || []).map(c => ({ id: null, label: c.label }))
      this.questions.splice(this.questions.indexOf(question) + 1, 0, copy)
      this.openKey = copy.key
      this.changed(true)
      return
    }
    if (event.target.closest('[data-del]')) {
      const message = question.answered
        ? 'Delete this question? Its answers stay on the responses already sent, but it leaves the survey and Results.'
        : 'Delete this question?'
      if (!window.confirm(message)) return
      this.state.questions = this.questions.filter(q => q !== question)
      this.dropFollowUpsOf(question.key)
      this.openKey = null
      this.changed(true)
    }
  }

  listInput(event) {
    const card = event.target.closest('.sv-q')
    if (!card) return
    const question = this.find(card.dataset.key)
    const field = event.target.dataset.f
    if (field === 'question') {
      question.question = event.target.value
      const label = card.querySelector('.sv-q-text')
      label.textContent = question.question || 'Untitled question'
      label.classList.toggle('is-blank', !question.question)
    } else if (field === 'help_text') {
      question.help_text = event.target.value
    } else if (event.target.dataset.choice !== undefined) {
      question.choices[Number(event.target.dataset.choice)].label = event.target.value
    } else {
      return
    }
    this.changed(false)
  }

  listChange(event) {
    const card = event.target.closest('.sv-q')
    if (!card) return
    const question = this.find(card.dataset.key)
    const field = event.target.dataset.f
    if (field === 'answer_type') {
      const before = question.answer_type
      if (question.answered && !question.answeredType) question.answeredType = before
      question.answer_type = event.target.value
      if (question.answer_type === 'rating') {
        if (before === 'multiple_choice') question.scale = 'custom'
        else question.scale = question.scale || 'satisfaction'
      } else {
        question.scale = null
      }
      if (['multiple_choice', 'checkboxes'].includes(question.answer_type) && !(question.choices || []).length) {
        question.choices = ['First choice', 'Second choice', 'Third choice'].map(label => ({ id: null, label }))
      }
      if (!this.isChoice(question)) this.dropFollowUpsOf(question.key)
    } else if (field === 'scale') {
      const labels = this.optionsOf(question)
      question.scale = event.target.value
      if (question.scale === 'custom' && !(question.choices || []).length) {
        question.choices = labels.slice(0, 7).map(label => ({ id: null, label }))
      } else if (question.scale === 'custom') {
        // Keep the rows (and their ids); take the preset's wording.
        question.choices = question.choices.map((c, i) => ({ id: c.id, label: labels[i] || c.label }))
      }
    } else if (field === 'required') {
      question.required = event.target.checked
    } else if (field === 'follow') {
      question.show_if = event.target.checked ? { key: this.sourcesBefore(question)[0].key, choices: [] } : null
    } else if (field === 'follow_source') {
      question.show_if = { key: event.target.value, choices: [] }
    } else {
      return
    }
    this.changed(true)
  }

  add(event) {
    const { type } = event.currentTarget.dataset
    const question = {
      key: this.newKey(),
      id: null,
      answer_type: type,
      scale: type === 'rating' ? 'satisfaction' : null,
      question: '',
      help_text: '',
      required: !['text', 'text_area'].includes(type),
      answered: false,
      show_if: null,
      choices: ['multiple_choice', 'checkboxes'].includes(type)
        ? ['First choice', 'Second choice', 'Third choice'].map(label => ({ id: null, label }))
        : []
    }
    this.questions.push(question)
    this.openKey = question.key
    this.changed(true)
    const input = this.element.querySelector(`#sv-qt-${CSS.escape(question.key)}`)
    if (input) {
      input.focus()
      input.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }

  field(event) {
    this.state[event.target.dataset.field] = event.target.value
    this.changed(false)
  }

  insertTag(event) {
    const button = event.currentTarget
    const box = this.element.querySelector(`#${button.dataset.into}`)
    const start = box.selectionStart != null ? box.selectionStart : box.value.length
    const end = box.selectionEnd != null ? box.selectionEnd : start
    box.value = box.value.slice(0, start) + button.dataset.tag + box.value.slice(end)
    box.focus()
    box.selectionStart = start + button.dataset.tag.length
    box.selectionEnd = box.selectionStart
    box.dispatchEvent(new Event('input', { bubbles: true }))
  }

  device(event) {
    this.deviceMode = event.currentTarget.dataset.device
    this.element.querySelectorAll('[data-device]').forEach(b => b.classList.toggle('is-on', b === event.currentTarget))
    this.phoneFrameTarget.hidden = this.deviceMode !== 'phone'
    this.wideFrameTarget.hidden = this.deviceMode !== 'wide'
    const from = this.deviceMode === 'phone' ? this.wideScreenTarget : this.screenTarget
    const to = this.deviceMode === 'phone' ? this.screenTarget : this.wideScreenTarget
    while (from.firstChild) to.appendChild(from.firstChild)
    this.styleDevice()
    this.fitScale()
  }

  // --- sorting -----------------------------------------------------------

  enableSorting() {
    if (typeof $ === 'undefined' || !$.fn || !$.fn.sortable) return
    $(this.listTarget).sortable({
      items: '> .sv-q',
      handle: '.sv-handle',
      tolerance: 'pointer',
      placeholder: 'sv-q-placeholder',
      forcePlaceholderSize: true,
      stop: () => {
        const order = Array.from(this.listTarget.querySelectorAll('.sv-q')).map(el => el.dataset.key)
        this.state.questions = order.map(key => this.find(key))
        // A follow-up has to come after the question it follows.
        this.state.questions = this.questions.map((q, i) => {
          if (!q.show_if) return q
          const at = this.questions.findIndex(s => s.key === q.show_if.key)
          return at < 0 || at >= i ? { ...q, show_if: null } : q
        })
        this.changed(true)
      }
    })
  }

  // --- saving and the preview --------------------------------------------

  newKey() {
    this.seq += 1
    return `new-${Date.now()}-${this.seq}`
  }

  payload() {
    return JSON.stringify(this.state)
  }

  changed(rerender) {
    if (rerender) this.render()
    this.dirty = true
    this.setStatus('Unsaved changes', 'is-dirty')
    this.refreshPreview(350)
  }

  setStatus(text, klass) {
    this.statusTarget.textContent = text
    this.statusTarget.className = `sv-savestate ${klass || ''}`
  }

  csrf() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  save() {
    this.setStatus('Saving…')
    this.errorsTarget.hidden = true
    fetch(this.data.get('saveUrl'), {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': this.csrf() },
      body: JSON.stringify({ builder: this.payload() })
    }).then(response => response.json().then(json => ({ ok: response.ok, json })))
      .then(({ ok, json }) => {
        if (!ok) {
          this.errorsTarget.innerHTML = (json.errors || ['Not saved.']).map(esc).join('<br>')
          this.errorsTarget.hidden = false
          this.setStatus('Not saved', 'is-error')
          return
        }
        const openIndex = this.questions.findIndex(q => q.key === this.openKey)
        this.state = json.builder
        this.openKey = openIndex >= 0 && this.questions[openIndex] ? this.questions[openIndex].key : null
        this.dirty = false
        this.render()
        this.setStatus('All changes saved')
        this.refreshPreview(0)
        const count = document.querySelector('.sv-tabs a.is-on small')
        if (count) count.textContent = this.questions.length
      })
      .catch(() => this.setStatus('Not saved: check your connection and try again', 'is-error'))
  }

  refreshPreview(delay) {
    clearTimeout(this.previewTimer)
    this.previewTimer = setTimeout(() => {
      const target = this.deviceMode === 'phone' ? this.screenTarget : this.wideScreenTarget
      const body = new FormData()
      body.append('builder', this.payload())
      const requestId = (this.previewRequest || 0) + 1
      this.previewRequest = requestId
      fetch(this.data.get('previewUrl'), {
        method: 'POST', credentials: 'same-origin', headers: { 'X-CSRF-Token': this.csrf() }, body
      }).then(response => (response.ok && !response.redirected ? response.text() : null)).then((html) => {
        if (html == null || requestId !== this.previewRequest) return
        const scroll = target.scrollTop
        target.innerHTML = html
        target.scrollTop = scroll
        this.styleDevice()
        this.fitScale()
      }).catch(() => {})
    }, delay)
  }

  styleDevice() {
    const page = this.element.querySelector('.sv-screen .rp')
    if (!page) return
    page.classList.toggle('rp-narrow', this.deviceMode === 'phone')
    page.classList.toggle('rp-wide', this.deviceMode === 'wide')
  }

  fitScale() {
    if (this.wideFrameTarget.hidden) return
    const box = this.scaleboxTarget
    const browser = box.querySelector('.sv-browser')
    const scale = Math.min(1, box.clientWidth / 900)
    browser.style.transform = `scale(${scale})`
    box.style.height = `${browser.offsetHeight * scale}px`
  }
}
